import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { MercenaryProvider, useMercenary } from './contexts/MercenaryContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ServerStatusProvider } from './contexts/ServerStatusContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import PrivacyPolicy from './components/Pages/PrivacyPolicy';
import Home from './components/Pages/Home';
import Landing from './components/Pages/Landing';
import PublicDiscoveryPage, { PUBLIC_DISCOVERY_PAGES } from './components/Pages/PublicDiscoveryPage';
import Welcome from './components/Pages/Welcome';
import CreateMercenary from './components/Pages/CreateMercenary/CreateMercenary';
import MainLayout from './components/Layout/MainLayout';
import AdminLayout from './components/Layout/AdminLayout';
import MercenaryProfile from './components/Pages/MercenaryLinks/MercenaryProfile';
import MercenaryAdvancements from './components/Pages/MercenaryLinks/MercenaryAdvancements';
import Market from './components/Pages/Market/Market';
import EmperorsVault from './components/Pages/EmperorsVault/EmperorsVault';
import Beasts from './components/Pages/Beast/Beasts';
import Inventory from './components/Pages/MercenaryLinks/Inventory';
import LevelUp from './components/Pages/LevelUp/LevelUp';
import Duel from './components/Pages/Duel/Duel';
import BattleResult from './components/Pages/BattleResult';
import BeastBattle from './components/Pages/Beast/BeastBattle';
import Bio from './components/Pages/MercenaryLinks/Bio';
import Attributes from './components/Pages/MercenaryLinks/Attributes';
import BattleHistory from './components/Pages/MercenaryLinks/BattleHistory';
import Statistics from './components/Pages/MercenaryLinks/Statistics';
import Profession from './components/Pages/MercenaryLinks/Profession';
import Achievements from './components/Pages/MercenaryLinks/Achievements';
import Activity from './components/Pages/MercenaryLinks/Activity';
import DailyMissions from './components/Pages/DailyMissions/DailyMissions';
import Mercenaries from './components/Pages/AccountLinks/Mercenaries';
import MyMercenaries from './components/Pages/MercenaryLinks/MyMercenaries';
import Notes from './components/Pages/AccountLinks/Notes';
import FriendsList from './components/Pages/AccountLinks/FriendsList';
import Mail from './components/Pages/AccountLinks/Mail';
import Account from './components/Pages/AccountLinks/Account';
import GameSettings from './components/Pages/AccountLinks/GameSettings';
import AuthInitializer from './AuthInitializer';
import TeamBattles from './components/Pages/TeamBattle/TeamBattles';
import CreateLobby from './components/Pages/TeamBattle/CreateLobby';
import LobbyView from './components/Pages/TeamBattle/LobbyView';
import RankedBattles from './components/Pages/RankedBattle/RankedBattles';
import CreateRankedLobby from './components/Pages/RankedBattle/CreateRankedLobby';
import Ranking from './components/Pages/Ranking/Ranking';
import CombatLeaderboards from './components/Pages/Ranking/CombatLeaderboards';
import HallOfHonor from './components/Pages/Ranking/HallOfHonor';
import DeathScreen from './components/Pages/DeathScreen';
import HospitalScreen from './components/Pages/Hospital/HospitalScreen';
import ViewTournaments from './components/Pages/Tournament/ViewTournaments';
import TournamentDetails from './components/Pages/Tournament/TournamentDetail';
import Adventure from './components/Pages/Adventure/Adventure';
import AdventureList from './components/Pages/Adventure/AdventureList';
import AdventureCompletion from './components/Pages/Adventure/AdventureCompletion';
import ChapterView from './components/Pages/Adventure/ChapterView';
import AuctionHouse from './components/Pages/AuctionHouse/AuctionHouse';
import Browse from './components/Pages/AuctionHouse/Browse';
import BuyOrder from './components/Pages/AuctionHouse/BuyOrder';
import SellOrder from './components/Pages/AuctionHouse/SellOrder';
import History from './components/Pages/AuctionHouse/History';
import Tavern from './components/Pages/Tavern/Tavern';

import Profile from './components/Pages/Profile/Profile';
import WorkDistrict from './components/Pages/WorkDistrict/WorkDistrict';
import CraftingDistrict from './components/Pages/Crafting/CraftingDistrict';

import Vault from './components/Pages/Vault/VaultPage';
import ContractBattles from './components/Pages/ContractBattle/ContractBattle';
import { GuildRoutes } from './components/Guild';
import CityProjects from './components/Pages/CityProjects/CityProjects';
import WikiPage from './components/Pages/Wiki/WikiPage';
import ItemDatabase from './components/Pages/ItemDatabase/ItemDatabase';
import GauntletHub from './components/Pages/Gauntlet/GauntletHub';
import GauntletRun from './components/Pages/Gauntlet/GauntletRun';
import GauntletReport from './components/Pages/Gauntlet/GauntletReport';
import GauntletHistory from './components/Pages/Gauntlet/GauntletHistory';
import { useActivityTracker } from './hooks/useActivityTracker';
import ServerStatusOverlay from './components/Layout/ServerStatusOverlay';

// Admin/GM pages: lazy-loaded so their code is NOT bundled into the main chunk
// and will never be downloaded by users who lack the required role.
const AdminPage = React.lazy(() => import('./components/Pages/Admin/AdminPage'));
const PostPatchNotes = React.lazy(() => import('./components/Pages/Admin/PostPatchNotes'));
const AssignItemsToVendor = React.lazy(() => import('./components/Pages/Admin/AssignItemsToVendor'));
const CreateTournament = React.lazy(() => import('./components/Pages/GameMaster/GameMasterTournament'));
const ManageUser = React.lazy(() => import('./components/Pages/Admin/ManageUser'));
const AuditLog = React.lazy(() => import('./components/Pages/Admin/AuditLog'));
const BattleViewer2D = React.lazy(() => import('./components/Pages/Admin/BattleViewer2D'));
const WorldEditor = React.lazy(() => import('./components/Pages/Admin/WorldEditor'));
const GameMasterPage = React.lazy(() => import('./components/Pages/GameMaster/GameMasterPage'));

const App: React.FC = () => {
  return (
    <ServerStatusProvider>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <MercenaryProvider>
              <TutorialProvider>
                <Router>
                  <AuthInitializer>
                    <ActivityTracker />
                    <ServerStatusOverlay />
                    <React.Suspense fallback={null}>
                    <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                {PUBLIC_DISCOVERY_PAGES.map((page) => (
                  <Route key={page.path} path={page.path} element={<PublicDiscoveryPage page={page} />} />
                ))}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* Protected Routes */}
                <Route path="/home" element={<MercenaryRequiredRoute><MainLayout><Home /></MainLayout></MercenaryRequiredRoute>}/>

                {/* Admin/GM Routes — lazy-loaded; code chunks are never downloaded by non-privileged users */}
                <Route path="/admin" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><AdminPage /></AdminLayout></ProtectedRoute>}/>
                <Route path="/audit" element={<ProtectedRoute roles={['Owner']}><AdminLayout><AuditLog /></AdminLayout></ProtectedRoute>}/>
                <Route path="/world-editor" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><WorldEditor /></AdminLayout></ProtectedRoute>}/>
                <Route path="/post-patch-note" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><PostPatchNotes /></AdminLayout></ProtectedRoute>}/>
                <Route path="/assign-items" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><AssignItemsToVendor /></AdminLayout></ProtectedRoute>}/>
                <Route path="/create-tournament" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><CreateTournament /></AdminLayout></ProtectedRoute>}/>
                <Route path="/manage-user" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><ManageUser /></AdminLayout></ProtectedRoute>}/>
                <Route path="/admin/battle-viewer" element={<ProtectedRoute roles={['Admin', 'Owner']}><AdminLayout><BattleViewer2D /></AdminLayout></ProtectedRoute>}/>
                <Route path="/gamemaster" element={<ProtectedRoute roles={['GameMaster', 'Admin', 'Owner']}><AdminLayout><GameMasterPage /></AdminLayout></ProtectedRoute>}/>
                <Route path="/level-up" element={<MercenaryRequiredRoute><MainLayout><LevelUp /></MainLayout></MercenaryRequiredRoute>}/>
                {/* Create mercenary should remain accessible with ProtectedRoute only */}
                <Route path="/create-mercenary" element={<ProtectedRoute><MainLayout><CreateMercenary /></MainLayout></ProtectedRoute>}/>
                <Route path="/mercenary" element={<MercenaryRequiredRoute><MainLayout><MercenaryProfile /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/daily-missions" element={<MercenaryRequiredRoute><MainLayout><DailyMissions /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/mercenaries" element={<MercenaryRequiredRoute><MainLayout><MyMercenaries /></MainLayout></MercenaryRequiredRoute>} />
                <Route path="/team-battle" element={<MercenaryRequiredRoute><MainLayout><TeamBattles /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/team-battle/create" element={<MercenaryRequiredRoute><MainLayout><CreateLobby /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/team-battle/lobby/:lobbyId" element={<MercenaryRequiredRoute><MainLayout><LobbyView /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/ranked-battle" element={<MercenaryRequiredRoute><MainLayout><RankedBattles /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/ranked-battle/create" element={<MercenaryRequiredRoute><MainLayout><CreateRankedLobby /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/market" element={<MercenaryRequiredRoute><MainLayout><Market /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/emperors-vault" element={<MercenaryRequiredRoute><MainLayout><EmperorsVault /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/beasts" element={<MercenaryRequiredRoute><MainLayout><Beasts /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/beast-battle" element={<MercenaryRequiredRoute><MainLayout><BeastBattle /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/inventory" element={<MercenaryRequiredRoute><MainLayout><Inventory /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/duel" element={<MercenaryRequiredRoute><MainLayout><Duel /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/battle-result/:battleId" element={<ProtectedRoute><MainLayout><BattleResult /></MainLayout></ProtectedRoute>}/>
                <Route path="/bio" element={<MercenaryRequiredRoute><MainLayout><Bio /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/attributes" element={<MercenaryRequiredRoute><MainLayout><Attributes /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/battle-history" element={<MercenaryRequiredRoute><MainLayout><BattleHistory /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/advancements" element={<MercenaryRequiredRoute><MainLayout><MercenaryAdvancements /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/statistics" element={<MercenaryRequiredRoute><MainLayout><Statistics /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/profession" element={<MercenaryRequiredRoute><MainLayout><Profession /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/achievements" element={<MercenaryRequiredRoute><MainLayout><Achievements /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/activity" element={<MercenaryRequiredRoute><MainLayout><Activity /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/mercenaries" element={<MercenaryRequiredRoute><MainLayout><Mercenaries /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/notes" element={<MercenaryRequiredRoute><MainLayout><Notes /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/friends" element={<MercenaryRequiredRoute><MainLayout><FriendsList /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/mail" element={<MercenaryRequiredRoute><MainLayout><Mail /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/account" element={<MercenaryRequiredRoute><MainLayout><Account /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/game-settings" element={<MercenaryRequiredRoute><MainLayout><GameSettings /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/ranking" element={<MercenaryRequiredRoute><MainLayout><Ranking /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/combat-leaderboards" element={<MercenaryRequiredRoute><MainLayout><CombatLeaderboards /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/hall-of-honor" element={<MercenaryRequiredRoute><MainLayout><HallOfHonor /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/deathscreen/:battleReportId" element={<ProtectedRoute><MainLayout><DeathScreen /></MainLayout></ProtectedRoute>}/>
                <Route path="/hospital/:mercenaryId" element={<MercenaryRequiredRoute><MainLayout><HospitalScreen /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/tournament" element={<MercenaryRequiredRoute><MainLayout><ViewTournaments /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/tournament/:id" element={<ProtectedRoute><MainLayout><TournamentDetails /></MainLayout></ProtectedRoute>}/>
                <Route path="/adventures" element={<MercenaryRequiredRoute><MainLayout><AdventureList /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/adventure/:adventure_id" element={<MercenaryRequiredRoute><MainLayout><Adventure /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/adventure-completion" element={<MercenaryRequiredRoute><MainLayout><AdventureCompletion /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/adventure/:adventureId/chapter/:chapterId" element={<MercenaryRequiredRoute><MainLayout><ChapterView /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/auction" element={<MercenaryRequiredRoute><MainLayout><AuctionHouse /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/auction/browse" element={<MercenaryRequiredRoute><MainLayout><Browse category={null} subcategory={null} searchTerm="" /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/auction/buy-order" element={<MercenaryRequiredRoute><MainLayout><BuyOrder /></MainLayout></MercenaryRequiredRoute>}/> 
                <Route path="/auction/sell-order" element={<MercenaryRequiredRoute><MainLayout><SellOrder /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/auction/history/:mercenaryId" element={<MercenaryRequiredRoute><MainLayout><History /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/tavern" element={<MercenaryRequiredRoute><MainLayout><Tavern /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/mercenary/:id" element={<MercenaryRequiredRoute><MainLayout><Profile /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/work" element={<MercenaryRequiredRoute><MainLayout><WorkDistrict /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/craft" element={<MercenaryRequiredRoute><MainLayout><CraftingDistrict /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/vault" element={<MercenaryRequiredRoute><MainLayout><Vault /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/contract-battle" element={<MercenaryRequiredRoute><MainLayout><ContractBattles /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/guild/*" element={<MercenaryRequiredRoute><MainLayout><GuildRoutes /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/city-projects" element={<MercenaryRequiredRoute><MainLayout><CityProjects /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/wiki" element={<ProtectedRoute><MainLayout><WikiPage /></MainLayout></ProtectedRoute>}/>
                <Route path="/wiki/:slug" element={<ProtectedRoute><MainLayout><WikiPage /></MainLayout></ProtectedRoute>}/>
                <Route path="/item-database" element={<ProtectedRoute><MainLayout><ItemDatabase /></MainLayout></ProtectedRoute>}/>
                <Route path="/gauntlet" element={<MercenaryRequiredRoute><MainLayout><GauntletHub /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/gauntlet/history" element={<MercenaryRequiredRoute><MainLayout><GauntletHistory /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/gauntlet/:runId" element={<MercenaryRequiredRoute><MainLayout><GauntletRun /></MainLayout></MercenaryRequiredRoute>}/>
                <Route path="/gauntlet/:runId/report" element={<MercenaryRequiredRoute><MainLayout><GauntletReport /></MainLayout></MercenaryRequiredRoute>}/>

                {/* Fallback Route */}
                <Route path="*" element={<Navigate to="/home" />} />
                    </Routes>
                    </React.Suspense>
                  </AuthInitializer>
                </Router>
              </TutorialProvider>
            </MercenaryProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </ServerStatusProvider>
  );
};

interface ProtectedRouteProps {
  children: JSX.Element;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" />;
  }

  if (roles && (!user.role || !roles.includes(user.role.name))) {
    return <Navigate to="/" />;
  }

  return children;
};

// Wrapper that shows Welcome page if user has no mercenary
const MercenaryRequiredRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { user } = useAuth();
  const { mercenary, mercenaries } = useMercenary();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Give time for mercenary context to load on initial mount only.
    // Once loaded, never go back to loading state, this prevents
    // rapid mount/unmount cycles when switching mercenaries.
    if (!isLoading) return;
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [mercenary, mercenaries, isLoading]);

  if (!user) {
    return <Navigate to="/" />;
  }

  if (roles && (!user.role || !roles.includes(user.role.name))) {
    return <Navigate to="/" />;
  }

  // Show loading briefly while context initializes
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a2e', color: '#fff' }}>Loading...</div>;
  }

  // Dead users must stay on the death screen (except when creating or switching mercenaries)
  const DEATH_SCREEN_EXEMPT = ['/create-mercenary', '/mercenaries'];
  if (user.isDead && user.deathBattleReport && !DEATH_SCREEN_EXEMPT.includes(location.pathname)) {
    return <Navigate to={`/deathscreen/${user.deathBattleReport}`} />;
  }

  // If no mercenaries exist and not on create-mercenary page, show welcome
  if (!mercenary && mercenaries.length === 0) {
    return <MainLayout><Welcome /></MainLayout>;
  }

  return children;
};

// Component to initialize activity tracking
const ActivityTracker: React.FC = () => {
  useActivityTracker();
  return null;
};

export default App;
